import { useTranslation } from 'react-i18next';
import { UserLoginStatus, UserType } from '@sib/api';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sib/ui';
import { CheckCircle, Copy, Trash } from '@sib/ui/icons';
import {
  getInitials,
  extractNameFromEmail,
  removeCompanyFromName,
} from '@sib/utils';
import { useCopyToClipboard } from '@spendbrain/common/hooks';
import { Invitee } from '@spendbrain/types/user';

interface InviteesListItemProps {
  data: Invitee;
  onRemove: () => void;
}

export const InviteesListItem = ({ data, onRemove }: InviteesListItemProps) => {
  const { t } = useTranslation();
  const { handleCopy } = useCopyToClipboard();

  const userName =
    removeCompanyFromName(data.name) || extractNameFromEmail(data.email);

  const isLoggedIn = data.userLoginStatus === UserLoginStatus.Completed;

  return (
    <li className="border-border grid grid-cols-[1fr_auto] items-center gap-x-2 gap-y-1 border-b px-3.5 py-2 last:border-none md:grid-cols-[280px_1fr_auto]">
      <div className="flex items-center gap-6">
        <Avatar className="size-[44px] shrink-0 bg-white">
          <AvatarImage src={data.imageUrl} />
          <AvatarFallback className="bg-muted">
            {getInitials(userName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1">
                  {isLoggedIn && (
                    <CheckCircle className="text-secondary size-5" />
                  )}
                  <p className="text-sm leading-5 font-semibold">{userName}</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isLoggedIn
                    ? t('components.shareDialog.loggedIn')
                    : t('components.shareDialog.userNotLoggedIn')}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleCopy(data.email)}
                  className="group flex cursor-pointer items-center gap-1 text-left"
                  type="button"
                  aria-label={t('components.shareDialog.clickToCopy')}
                >
                  <span className="text-sm leading-5 transition-all group-hover:font-semibold">
                    {data.email}
                  </span>
                  <Copy className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('components.shareDialog.clickToCopy')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      {!isLoggedIn && (
        <Button
          onClick={onRemove}
          variant="outline"
          className="lowercase md:col-start-3"
        >
          <span className="hidden md:inline">
            {t('components.shareDialog.remove')}
          </span>
          <Trash className="size-4 md:hidden" />
        </Button>
      )}
      {data.userType === UserType.External && data.organization && (
        <p className="text-muted-foreground col-span-2 truncate pl-[68px] text-sm md:col-span-1 md:col-start-2 md:row-start-1 md:self-end md:pl-0">
          {data.organization}
        </p>
      )}
    </li>
  );
};
